import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'

import { useI18n } from '../../context/I18nContext'
import { formatTND } from '../../lib/currency'
import { normalizeImageList } from '../../lib/imageData'
import { shouldShowPrices } from '../../lib/priceVisibility'
import { resolveProductName } from '../../lib/productRelations'
import { getProductBrand } from '../../lib/productBrand'
import { getBrandImageSrc } from '../../lib/brandImage'
import AnimatedEyebrow from '../AnimatedEyebrow'
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader'

export default function ProductRelatedProductsSection({ items = [] }) {
  const { t, language } = useI18n()
  const visibleItems = Array.isArray(items) ? items.slice(0, 3) : []
  const showPrices = shouldShowPrices()

  if (visibleItems.length === 0) {
    return null
  }

  return (
    <section className="relative overflow-hidden">
      <div className="relative z-10">
        <div className="mb-10 flex flex-col items-center gap-4 text-center">
          <div className="max-w-2xl">
            <AnimatedEyebrow
              className="mb-6 justify-center"
              borderClassName="border-[var(--dash-secondary)]"
              surfaceClassName="bg-[var(--dash-black)]"
              dotClassName="bg-[var(--dash-main)] shadow-[0_0_8px_#ffffff]"
            >
              {t('productDetails.relatedProductsEyebrow', 'Keep exploring')}
            </AnimatedEyebrow>
            <h2 className="text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
              <span className="">{t('productDetails.relatedProductsLine1 ', 'Related ')}</span>
              <span className="font-black text-white">
                {t('productDetails.relatedProductsLine2', 'Machines')}
              </span>
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-white/95 sm:text-base">
              {t(
                'productDetails.relatedProductsDescription',
                'Explore other machines with a similar type or matching spare-part compatibility.',
              )}
            </p>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {visibleItems.map((product, index) => {
            const productId = String(product?._id || product?.id || '').trim()
            const productName = resolveProductName(product, language)
            const previewImage = normalizeImageList(product?.images)[0]
            const isInStock = Number(product?.stock ?? 0) > 0
            const brand = getProductBrand(product)
            const brandImageSrc = getBrandImageSrc(brand)
            const model = String(product?.specs?.model || '').trim()

            return (
              <Link
                key={productId}
                href={`/machines/${productId}`}
                className="group relative flex flex-col overflow-hidden rounded-[26px] border border-white/10 bg-[var(--dash-black)] shadow-[0_22px_55px_rgba(0,0,0,0)] transition-all duration-300 hover:border-[var(--dash-main)]/40 hover:shadow-[0_30px_70px_rgba(0,0,0,0)]"
              >
                <div className="relative z-0 aspect-[4/3] overflow-hidden bg-[#111]">
                  {previewImage ? (
                    <ImageOnlySkeletonLoader
                      image={previewImage}
                      alt={productName || t('productDetails.imageAlt', 'Machine image')}
                      containerClassName="h-full w-full"
                      imgClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110 !z-0"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs font-bold uppercase tracking-[0.2em] text-white/25">
                      {t('common.noImage')}
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-55" />
                  <span className="absolute left-3 top-3 rounded-full border border-white/10 bg-black/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span
                    className={`absolute right-3 top-3 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] shadow-sm backdrop-blur ${
                      isInStock
                        ? 'border-emerald-400/40 bg-black/70 text-emerald-300'
                        : 'border-rose-400/40 bg-black/70 text-rose-300'
                    }`}
                  >
                    {isInStock
                      ? t('productDetails.inStock', 'In stock')
                      : t('productDetails.outOfStock', 'Out of stock')}
                  </span>
                </div>
                <div className="relative flex flex-col gap-3.5 border-t border-white/5 p-5">
                  <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-[var(--dash-main)]/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-white transition-colors duration-300 group-hover:text-[var(--dash-main)]">
                      {productName || productId}
                    </h3>
                    {brandImageSrc ? (
                      <span className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-white/5 p-1.5 transition-transform duration-300 group-hover:scale-110 group-hover:border-[var(--dash-main)]/40">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={brandImageSrc} alt={`${brand} logo`} className="h-full w-full object-contain" />
                      </span>
                    ) : null}
                  </div>
                  {model ? (
                    <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-white/50">
                      <span className="h-1 w-1 rounded-full bg-[var(--dash-main)]" />
                      {model}
                    </span>
                  ) : null}
                  <p className="text-sm font-semibold text-[var(--dash-other)]">
                    {showPrices && Number(product?.price) > 0
                      ? formatTND(product.price)
                      : t('common.priceOnRequest', 'Price on request')}
                  </p>
                  <span className="inline-flex w-fit items-center gap-2 rounded-xl border border-white/20 px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/82 shadow-[0_0_0_rgba(0,0,0,0)] transition-all duration-300 group-hover:border-[var(--dash-main)] group-hover:bg-[var(--dash-main)] group-hover:text-white group-hover:shadow-[0_12px_30px_-8px_rgba(var(--dash-main-rgb),0.6)]">
                    <ArrowUpRight className="h-3.5 w-3.5 text-[var(--dash-main)] transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
                    {t('productsPage.grid.viewDetails')}
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}
